import { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

export interface OverflowItem {
  id: string;
  label: string;
  /** Extra detail (e.g. role) shown only in the hover card, never on inline pills. */
  hint?: string;
}

/**
 * Linear-style tag list: shows the first `visibleCount` items inline and
 * collapses the rest into a "+N" badge. Hovering anywhere on the cell opens a
 * card listing the full set with their hints — inline pills always stay
 * label-only so rows keep a uniform, single-line height.
 */
export function BadgeOverflow({
  items,
  visibleCount = 1,
  empty,
}: {
  items: OverflowItem[];
  visibleCount?: number;
  empty?: ReactNode;
}) {
  if (items.length === 0) return <>{empty ?? <span className="text-muted-foreground">—</span>}</>;

  const shown = items.slice(0, visibleCount);
  const rest = items.slice(visibleCount);

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <div tabIndex={0} className="flex items-center gap-1 min-w-0 cursor-default outline-none">
          {shown.map((i) => (
            <Badge key={i.id} variant="secondary" className="max-w-[14rem] truncate whitespace-nowrap">
              {i.label}
            </Badge>
          ))}
          {rest.length > 0 && (
            <Badge
              variant="outline"
              className="shrink-0 cursor-default whitespace-nowrap text-muted-foreground"
            >
              +{rest.length}
            </Badge>
          )}
        </div>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-64 p-2">
        <div className="flex flex-col gap-1">
          {items.map((i) => (
            <div key={i.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="truncate">{i.label}</span>
              {i.hint && <span className="text-xs text-muted-foreground shrink-0">{i.hint}</span>}
            </div>
          ))}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
